import { CoinIcon } from './CoinIcon';

/**
 * The small header/footer pill: a coin icon plus a formatted balance.
 * Used by the BrandHeader when a reader is selected. Intentionally
 * compact — the number is the information, the coin is the glyph.
 *
 * Accepts `balance: null` to render a muted "—" placeholder so the
 * chip doesn't vanish on cold loads while the server read is in flight.
 */
export interface CoinBadgeProps {
  balance: number | null;
  /** Optional click handler — e.g. open the history flyout. */
  onClick?: () => void;
  /** Render smaller variant for dense toolbars. */
  size?: 'sm' | 'md';
  className?: string;
  /** Override the pill's aria label (default: "N coins earned"). */
  ariaLabel?: string;
}

const numberFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});

export function CoinBadge({ balance, onClick, size = 'md', className, ariaLabel }: CoinBadgeProps) {
  const isInteractive = typeof onClick === 'function';
  const displayed = balance === null ? '—' : numberFormatter.format(Math.max(balance, 0));
  const label =
    ariaLabel ??
    (balance === null
      ? 'Coin balance loading'
      : `${numberFormatter.format(Math.max(balance, 0))} coins earned`);

  const iconSize = size === 'sm' ? 14 : 16;
  const padY = size === 'sm' ? '2px' : '4px';
  const padX = size === 'sm' ? '8px' : '10px';
  const fontSize = size === 'sm' ? '11px' : '12px';

  const commonProps = {
    'aria-label': label,
    className: [
      'inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 font-semibold text-amber-900 transition-colors',
      isInteractive
        ? 'hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50'
        : '',
      className ?? '',
    ]
      .filter(Boolean)
      .join(' '),
    style: {
      padding: `${padY} ${padX}`,
      fontSize,
      lineHeight: 1,
    } as const,
  };

  const content = (
    <>
      <CoinIcon size={iconSize} inert />
      <span>{displayed}</span>
    </>
  );

  if (isInteractive) {
    return (
      <button type="button" onClick={onClick} {...commonProps}>
        {content}
      </button>
    );
  }
  return <span {...commonProps}>{content}</span>;
}
